import React, { FC, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import {
  REFRESH_EXECUTIVE_KPI_CACHE,
  INVOKE_ROLLUP_COORDINATOR,
  INVOKE_ROLLUP_BACKFILL,
  BACKFILL_RUN_STATUS,
  EXECUTIVE_PERFORMANCE_SUMMARY_QUERY,
  InvokeRollupCoordinatorData,
  InvokeRollupCoordinatorVars,
  InvokeRollupBackfillData,
  InvokeRollupBackfillVars,
} from '@/graph/group-performance';
import { useBackfillRunStatusPolling } from '@/hooks/useBackfillRunStatusPolling';
import { RunStatusLine } from '@/components/core/RunStatusLine';

interface ExecutiveAdminDialogProps {
  open: boolean;
  onClose: () => void;
}

const ExecutiveAdminDialog: FC<ExecutiveAdminDialogProps> = ({ open, onClose }) => {
  const client = useApolloClient();
  const toaster = useToaster();

  // Cache refresh state
  const [refreshingCache, setRefreshingCache] = useState(false);

  // Coordinator state
  const [invokingCoordinator, setInvokingCoordinator] = useState(false);
  const [coordinatorDryRun, setCoordinatorDryRun] = useState(false);
  const coordinatorPolling = useBackfillRunStatusPolling(BACKFILL_RUN_STATUS, 'Coordinator');

  // Backfill state
  const [invokingBackfill, setInvokingBackfill] = useState(false);
  const [backfillDays, setBackfillDays] = useState(30);
  const [backfillMode, setBackfillMode] = useState<'missing_only' | 'overwrite_all'>('missing_only');
  const [backfillDryRun, setBackfillDryRun] = useState(false);
  const backfillPolling = useBackfillRunStatusPolling(BACKFILL_RUN_STATUS, 'Backfill');

  const handleRefreshCache = useCallback(async () => {
    setRefreshingCache(true);
    try {
      await client.mutate({ mutation: REFRESH_EXECUTIVE_KPI_CACHE });
      await client.refetchQueries({ include: [EXECUTIVE_PERFORMANCE_SUMMARY_QUERY] });
      toaster.success('Executive KPI cache refreshed');
    } catch (err: any) {
      toaster.error(`Cache refresh failed: ${err.message}`);
    } finally {
      setRefreshingCache(false);
    }
  }, [client, toaster]);

  const handleInvokeCoordinator = useCallback(async () => {
    setInvokingCoordinator(true);
    coordinatorPolling.reset();
    try {
      const { data } = await client.mutate<InvokeRollupCoordinatorData, InvokeRollupCoordinatorVars>({
        mutation: INVOKE_ROLLUP_COORDINATOR,
        variables: { dryRun: coordinatorDryRun },
      });
      const result = data?.invokeRollupCoordinator;
      const needsPolling = result?.message?.includes('Poll backfillRunStatus');
      if (result?.success) {
        if (needsPolling && result.runId) {
          toaster.success('Coordinator started');
          coordinatorPolling.start(result.runId);
        } else {
          toaster.success(`Coordinator invoked${coordinatorDryRun ? ' (dry run)' : ''}`);
        }
      } else {
        toaster.error(`Coordinator failed: ${result?.message ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      toaster.error(`Coordinator invocation failed: ${err.message}`);
    } finally {
      setInvokingCoordinator(false);
    }
  }, [client, toaster, coordinatorDryRun, coordinatorPolling]);

  const handleInvokeBackfill = useCallback(async () => {
    setInvokingBackfill(true);
    backfillPolling.reset();
    try {
      const { data } = await client.mutate<InvokeRollupBackfillData, InvokeRollupBackfillVars>({
        mutation: INVOKE_ROLLUP_BACKFILL,
        variables: { backfillDays, backfillMode, dryRun: backfillDryRun },
      });
      const result = data?.invokeRollupBackfill;
      const needsPolling = result?.message?.includes('Poll backfillRunStatus');
      if (result?.success) {
        if (needsPolling && result.runId) {
          toaster.success('Backfill started');
          backfillPolling.start(result.runId);
        } else {
          toaster.success('Backfill complete');
        }
      } else {
        toaster.error(`Backfill failed: ${result?.message ?? 'Unknown error'}`);
      }
    } catch (err: any) {
      toaster.error(`Backfill invocation failed: ${err.message}`);
    } finally {
      setInvokingBackfill(false);
    }
  }, [client, toaster, backfillDays, backfillMode, backfillDryRun, backfillPolling]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Administration Options</DialogTitle>
      <DialogContent>
        {/* Cache Refresh */}
        <Box py={1}>
          <Typography variant="subtitle2">Cache Management</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Clear the executive KPI rollup cache.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleRefreshCache}
            disabled={refreshingCache}
          >
            {refreshingCache ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            Refresh Cache
          </Button>
        </Box>

        <Divider />

        {/* Coordinator */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Coordinator</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Invoke Coordinator function that calculates daily executive KPI rollup for past.
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 16 }} mb={1.5} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={coordinatorDryRun}
                  onChange={e => setCoordinatorDryRun(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeCoordinator}
            disabled={invokingCoordinator || coordinatorPolling.isPolling}
          >
            {invokingCoordinator || coordinatorPolling.isPolling ? (
              <CircularProgress size={16} style={{ marginRight: 8 }} />
            ) : null}
            Invoke Coordinator
          </Button>
          {coordinatorPolling.status && coordinatorPolling.runId && (
            <RunStatusLine status={coordinatorPolling.status} />
          )}
        </Box>

        <Divider />

        {/* Backfill */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Backfill</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Invoke the backfill functions to recompute daily executive KPI rollups for a configurable number of past
            days.
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 16 }} mb={1.5} flexWrap="wrap">
            <FormControl size="small" style={{ minWidth: 100 }}>
              <TextField
                label="Days"
                type="number"
                size="small"
                variant="outlined"
                value={backfillDays}
                onChange={e => setBackfillDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 365 }}
                style={{ width: 90 }}
              />
            </FormControl>
            <FormControl size="small" variant="outlined" style={{ minWidth: 100 }}>
              <Select
                value={backfillMode}
                onChange={e => setBackfillMode(e.target.value as 'missing_only' | 'overwrite_all')}
              >
                <MenuItem value="missing_only">Missing only</MenuItem>
                <MenuItem value="overwrite_all">Overwrite all</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox checked={backfillDryRun} onChange={e => setBackfillDryRun(e.target.checked)} size="small" />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeBackfill}
            disabled={invokingBackfill || backfillPolling.isPolling}
          >
            {invokingBackfill || backfillPolling.isPolling ? (
              <CircularProgress size={16} style={{ marginRight: 8 }} />
            ) : null}
            Invoke Backfill
          </Button>
          {backfillPolling.status && backfillPolling.runId && <RunStatusLine status={backfillPolling.status} />}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutiveAdminDialog;
